import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { socket } from '@/socket';
import type { MediaParentEntity } from '@/types/dtos/media/media-parent-entity.enum';
import type { MediaProcessingUpdatedNotification } from '@/types/media/media-processing-updated.notification';
import type { MediaAssetApprovalUpdatedNotification } from '@/types/media/media-asset-approval-updated.notification';
import { invalidateMediaUploadQueries } from '@/state/mutations/media/invalidate-media-upload-queries';
import { patchMediaAssetApprovalInQueryCache } from '@/state/mutations/media/patch-media-asset-approval-cache';

/** If the join ACK never arrives, clear the in-flight lock and retry. */
const JOIN_ACK_TIMEOUT_MS = 8_000;
const MAX_JOIN_ATTEMPTS = 3;

/**
 * Join `media-parent-{type}-{parentId}` on the flows Socket.IO namespace and refetch media
 * when the server emits `mediaProcessingUpdated` (queued → inspecting → rejected/succeeded).
 * Also applies `mediaAssetApprovalUpdated` immediately for peer Assets approval badges.
 */
export function useMediaProcessingSocketSync(parentType: MediaParentEntity | undefined, parentId: string | undefined) {
  const queryClient = useQueryClient();
  const { getToken } = useAuth();

  useEffect(() => {
    if (!parentType || !parentId) return;
    let disposed = false;
    let joinRequested = false;
    let joinedParentId: string | undefined;
    let joinAttempts = 0;
    let joinAckTimeoutId: ReturnType<typeof setTimeout> | undefined;

    const clearJoinAckTimeout = () => {
      if (joinAckTimeoutId !== undefined) {
        clearTimeout(joinAckTimeoutId);
        joinAckTimeoutId = undefined;
      }
    };

    const onMediaProcessingUpdated = (notification: MediaProcessingUpdatedNotification) => {
      invalidateMediaUploadQueries(queryClient, notification.mediaId);
    };

    const onMediaAssetApprovalUpdated = (notification: MediaAssetApprovalUpdatedNotification) => {
      if (notification.parentId !== parentId) return;
      if (String(notification.parentType).toLowerCase() !== String(parentType).toLowerCase()) return;
      patchMediaAssetApprovalInQueryCache(queryClient, {
        mediaId: notification.mediaId,
        assetVersion: notification.assetVersion,
        isApproved: notification.isApproved,
        sessionOriginId: notification.sessionOriginId,
      });
    };

    socket.on('mediaProcessingUpdated', onMediaProcessingUpdated);
    socket.on('mediaAssetApprovalUpdated', onMediaAssetApprovalUpdated);

    const joinRoom = async () => {
      try {
        const token = await getToken();
        if (disposed) return;
        if (token && !socket.connected) {
          socket.io.opts.extraHeaders = { Authorization: `Bearer ${token}` };
          socket.connect();
        }
        if (!socket.connected) return;
        if (joinRequested || joinedParentId) return;
        if (joinAttempts >= MAX_JOIN_ATTEMPTS) return;

        joinRequested = true;
        joinAttempts += 1;
        clearJoinAckTimeout();
        joinAckTimeoutId = setTimeout(() => {
          joinAckTimeoutId = undefined;
          joinRequested = false;
          // ACK dropped or never sent — allow another attempt while the socket stays up.
          if (!disposed && !joinedParentId) {
            void joinRoom();
          }
        }, JOIN_ACK_TIMEOUT_MS);

        socket.emit('mediaParentJoinRoom', { parentType, parentId }, (result?: { ok?: boolean; parentId?: string }) => {
          clearJoinAckTimeout();
          joinRequested = false;
          if (!result?.ok || !result.parentId) return;
          if (disposed) {
            socket.emit('mediaParentLeaveRoom', { parentType, parentId: result.parentId });
            return;
          }
          joinedParentId = result.parentId;
          joinAttempts = 0;
        });
      } catch {
        clearJoinAckTimeout();
        joinRequested = false;
        // non-fatal: HTTP GET still works
      }
    };

    const onConnect = () => {
      clearJoinAckTimeout();
      joinedParentId = undefined;
      joinRequested = false;
      joinAttempts = 0;
      void joinRoom();
    };
    const onDisconnect = () => {
      clearJoinAckTimeout();
      joinRequested = false;
      joinedParentId = undefined;
    };
    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    void joinRoom();

    return () => {
      disposed = true;
      clearJoinAckTimeout();
      if (joinedParentId) {
        socket.emit('mediaParentLeaveRoom', { parentType, parentId: joinedParentId });
      }
      socket.off('mediaProcessingUpdated', onMediaProcessingUpdated);
      socket.off('mediaAssetApprovalUpdated', onMediaAssetApprovalUpdated);
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
    };
  }, [parentType, parentId, getToken, queryClient]);
}
